import { ref } from 'vue'
import { defineStore } from 'pinia'

import type { PollingCenter } from '../models/polling-center.model'

import PollingCenterApiService from '../services/polling-center-api.service'
import { exportToCSV } from '@/modules/core/utils/export.util'

const pollingStationService = new PollingCenterApiService()

export const usePollingCenterIndexStore = defineStore('pollingCenterIndex', () => {

    const mIsLoading = ref<boolean>(false)

    const mPollingCenters = ref<PollingCenter[]>([])
    const mSelectedPollingCenters = ref<PollingCenter[]>([])

    const mRegions = ref<string[]>([])
    const mConstituencies = ref<string[]>([])

    let mApiInterval: ReturnType<typeof setInterval> | null = null;

    // Start the interval
    function startInterval() {
        if (!mApiInterval) {
            mApiInterval = setInterval(getPollingCenters, 30000); // 10 seconds
        }
    }

    // Stop the interval
    function stopInterval() {
        if (mApiInterval) {
            clearInterval(mApiInterval);
            mApiInterval = null;
        }
    }

    function exportDataAsCsv() {
        const slips = (mSelectedPollingCenters.value.length < 1) ? mPollingCenters.value : mSelectedPollingCenters.value
        exportToCSV(slips, 'polling_centers')
    }

    const getPollingCenters = async () => {
        mIsLoading.value = true

        const result = await pollingStationService.getPollingCenters()

        result.fold(
            (value) => {
                mPollingCenters.value = value as PollingCenter[]
                mRegions.value = extractRegions(mPollingCenters.value)
                mConstituencies.value = extractConstituencies(mPollingCenters.value)
                mIsLoading.value = false;
            },
            (error) => {
                mIsLoading.value = false;
                console.error(error);
            }
        )
    }

    function extractRegions(centers?: PollingCenter[] | null): string[] {
        if (centers) {
            const regions = centers
                .map((center: PollingCenter) => center.region)
                .filter((region) => region !== undefined) // Filter out undefined regions
            return Array.from(new Set(regions))
                .sort((a, b) => a[0].localeCompare(b[0])) // Sort the regions by name
                .map((region) => region) // Remove duplicates with incremental id
        }
        return []
    }

    function extractConstituencies(centers?: PollingCenter[] | null): string[] {
        if (centers) {
            const constituencies = centers
                .map((center: PollingCenter) => center.constituency)
                .filter((constituency) => constituency !== undefined)

            return Array.from(new Set(constituencies))
                .sort((a, b) => a[0].localeCompare(b[0])) // Sort by constituency name
                .map((constituency) => constituency)
        }
        return []
    }

    return {
        isLoading: mIsLoading,
        pollingCenters: mPollingCenters,
        selectedPollingCenters: mSelectedPollingCenters,
        regions: mRegions,
        constituencies: mConstituencies,
        getPollingCenters,
        startInterval,
        stopInterval,
        exportDataAsCsv
    }
})